/**
 * Medications API
 */
import { apiClient } from '../api-client';
import type { MedicationsResponse, MedicationLog } from './types';

export const medicationsApi = {
  /**
   * Get patient medication schedules and today's logs
   */
  getMedications: async (patientId: number): Promise<MedicationsResponse> => {
    return apiClient.get(`/medications/patient/${patientId}`);
  },

  /**
   * ✅ NEW: Get current patient's medications (no patient_id needed)
   */
  getMyMedications: async (): Promise<MedicationsResponse> => {
    return apiClient.get(`/medications/me/medications`);
  },

  /**
   * Log medication intake
   */
  logMedication: async (
    patientId: number,
    medicationId: number,
    data: {
      medication_schedule_id: number;
      scheduled_time: string;
      taken_time?: string;
      status: 'taken' | 'missed' | 'skipped';
      date: string;
      notes?: string;
    }
  ): Promise<MedicationLog> => {
    return apiClient.post(
      `/medications/patients/${patientId}/medications/${medicationId}/log`,
      data
    );
  },

  /**
   * Get medication history
   */
  getMedicationHistory: async (
    patientId: number,
    days: number = 30
  ): Promise<{ logs: MedicationLog[] }> => {
    return apiClient.get(`/medications/patients/${patientId}/medications/history`, { days });
  },

  /**
   * Create medication schedule (Doctor only)
   */
  createMedicationSchedule: async (data: {
    patient_id: number;
    medication_name: string;
    dosage: string;
    frequency: string;
    category?: string;
    scheduled_times: string[];
    instructions?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    return apiClient.post('/medications/schedules', data);
  },

  /**
   * ✅ NEW: Update medication Schedule
   */
  updateMedicationSchedule: async (scheduleId: number, data: {
    medication_name: string;
    dosage: string;
    frequency: string;
    scheduled_times: string[];
    instructions?: string;
  }): Promise<{ status: string; message: string; scheduleId: number }> => {
    return apiClient.put(`/medications/schedules/${scheduleId}`, data);
  },

  /**
   * ✅ NEW: Delete medication Schedule
   */
  deleteMedicationSchedule: async (scheduleId: number): Promise<{ status: string; message: string; scheduleId: number }> => {
    return apiClient.delete(`/medications/schedules/${scheduleId}/`);
  },

  /**
   * ✅ NEW: Get current patient's medication logs
   */
  getMyMedicationLogs: async (days: number = 7): Promise<{ patient_id: number; logs: any[] }> => {
    return apiClient.get(`/medications/me/logs`, { days });
  },

  /**
   * ✅ NEW: Update medication log
   */
  updateMyMedicationLog: async (logId: number, data: {
    scheduled_time?: string;
    taken_time?: string;
    status?: 'TAKEN' | 'MISSED' | 'SKIPPED' | 'PENDING';  // ✅ FIX: Backend expects uppercase
    notes?: string;
  }): Promise<{ status: string; message: string; log_id: number }> => {
    return apiClient.put(`/medications/me/medications/logs/${logId}`, data);
  },

  /**
   * ✅ NEW: Delete medication log
   */
  deleteMyMedicationLog: async (logId: number): Promise<{ status: string; message: string; log_id: number }> => {
    return apiClient.delete(`/medications/me/medications/logs/${logId}`);
  },

  /**
   * ✅ NEW: Log medication intake for current patient
   */
  logMyMedicationIntake: async (data: {
    medication_schedule_id: number;
    date: string;
    scheduled_time: string;
    taken_time?: string;
    status: 'TAKEN' | 'MISSED' | 'SKIPPED' | 'PENDING';  // ✅ FIX: Backend expects uppercase
    notes?: string;
  }): Promise<MedicationLog> => {
    return apiClient.post('/medications/me/log', data);
  },

  /**
   * ✅ NEW: Log medication for current patient (simplified - auto-adds date)
   */
  logMyMedication: async (data: {
    medication_schedule_id: number;
    scheduled_time: string;
    taken_time?: string;
    status: 'TAKEN' | 'MISSED' | 'SKIPPED' | 'PENDING';  // ✅ FIX: Backend expects uppercase
    notes?: string;
  }): Promise<MedicationLog> => {
    // Automatically add today's date
    const today = new Date().toISOString().split('T')[0];
    return apiClient.post('/medications/me/log', {
      ...data,
      date: today,
    });
  },
};